import type {
  InspectedRequest,
  LLMProviderInterface as Provider,
  ModelCapabilities,
  ProviderFactoryConfig,
  ProviderModelInfo,
  ProviderRequest,
  ProviderResponse,
  ProviderStreamChunk,
  ProviderUsage,
  ProviderUsagePricing,
} from '@standardagents/spec';
import { ProviderError } from '@standardagents/spec';
import {
  attachUsagePricing,
  buildChatParams,
  createChatErrorChunk,
  createChatStreamState,
  parseChatSSEStream,
  transformChatResponse,
  truncateChatBase64,
} from './chat-completions-transformers';
import { getNovitaIconDataUri, getNovitaModelIconDataUri } from './icons';

export { ProviderError };

export interface NovitaModelInfo {
  id: string;
  object?: string;
  created?: number;
  title?: string;
  description?: string;
  context_size?: number;
  input_token_price_per_m?: number;
  output_token_price_per_m?: number;
  pricing?: {
    prompt?: NovitaPriceInfo;
    completion?: NovitaPriceInfo;
    input_cache_read?: NovitaPriceInfo;
  };
}

const DEFAULT_BASE_URL = 'https://api.novita.ai/openai/v1';
const NOVITA_PRICE_SCALE = 10_000;

interface NovitaPriceInfo {
  origin_price_per_m?: number;
  price_per_m?: number;
}

const STATIC_MODELS: NovitaModelInfo[] = [
  {
    id: 'zai-org/glm-5.2',
    title: 'zai-org/glm-5.2',
    description: 'Large long-context model on Novita for coding, reasoning, and planning.',
    context_size: 1_048_576,
    input_token_price_per_m: 14_000,
    output_token_price_per_m: 44_000,
  },
  {
    id: 'moonshotai/kimi-k2.7-code',
    title: 'moonshotai/kimi-k2.7-code',
    description: 'Large coding and agentic tool-use model on Novita.',
    context_size: 262_144,
    input_token_price_per_m: 9_500,
    output_token_price_per_m: 40_000,
  },
  {
    id: 'minimax/minimax-m3',
    title: 'minimax/minimax-m3',
    description: 'Large general-purpose model on Novita for agents, tool use, and long context.',
    context_size: 1_000_000,
    input_token_price_per_m: 3_000,
    output_token_price_per_m: 12_000,
  },
  {
    id: 'deepseek/deepseek-v4-pro',
    title: 'deepseek/deepseek-v4-pro',
    description: 'Large reasoning and planning model on Novita.',
    context_size: 1_048_576,
    input_token_price_per_m: 16_000,
    output_token_price_per_m: 32_000,
  },
  {
    id: 'deepseek/deepseek-v4-flash',
    title: 'deepseek/deepseek-v4-flash',
    description: 'Cost-effective long-context DeepSeek model on Novita.',
    context_size: 1_048_576,
    input_token_price_per_m: 1_400,
    output_token_price_per_m: 2_800,
  },
  {
    id: 'deepseek/deepseek-v3.2',
    title: 'deepseek/deepseek-v3.2',
    description: 'General reasoning and planning model on Novita.',
    context_size: 163_840,
    input_token_price_per_m: 2_690,
    output_token_price_per_m: 4_000,
  },
  {
    id: 'nvidia/nemotron-3-nano-30b-a3b',
    title: 'nvidia/nemotron-3-nano-30b-a3b',
    description: 'Low-latency Novita model for lightweight extraction and classification.',
    context_size: 262_144,
    input_token_price_per_m: 500,
    output_token_price_per_m: 2_000,
  },
];

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

function includesInsensitive(haystack: string | undefined, needle: string): boolean {
  return haystack?.toLowerCase().includes(needle.toLowerCase()) ?? false;
}

function isVisionModel(model: NovitaModelInfo): boolean {
  const searchable = `${model.id} ${model.title || ''} ${model.description || ''}`;
  return (
    includesInsensitive(searchable, 'vision') ||
    includesInsensitive(searchable, 'visual') ||
    includesInsensitive(searchable, 'multimodal') ||
    includesInsensitive(searchable, 'image') ||
    includesInsensitive(searchable, 'ocr') ||
    /\bvl\b/i.test(searchable)
  );
}

export function normalizeNovitaPricePerMillion(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return Number((value / NOVITA_PRICE_SCALE).toFixed(12));
}

export function novitaUsagePricingFromModel(
  model: NovitaModelInfo | undefined
): ProviderUsagePricing | undefined {
  const inputPerMillion = normalizeNovitaPricePerMillion(
    model?.pricing?.prompt?.price_per_m ?? model?.input_token_price_per_m
  );
  const outputPerMillion = normalizeNovitaPricePerMillion(
    model?.pricing?.completion?.price_per_m ?? model?.output_token_price_per_m
  );
  if (inputPerMillion === undefined || outputPerMillion === undefined) {
    return undefined;
  }
  const cachedInputPerMillion = normalizeNovitaPricePerMillion(
    model?.pricing?.input_cache_read?.price_per_m
  );
  const pricing: ProviderUsagePricing = {
    inputPerMillion,
    outputPerMillion,
  };
  if (cachedInputPerMillion !== undefined) {
    pricing.cachedInputPerMillion = cachedInputPerMillion;
  }
  return pricing;
}

export function calculateNovitaUsageCost(
  usage: ProviderUsage,
  pricing?: ProviderUsagePricing
): number | undefined {
  if (
    !pricing ||
    typeof pricing.inputPerMillion !== 'number' ||
    typeof pricing.outputPerMillion !== 'number'
  ) {
    return undefined;
  }

  const cachedTokens = usage.cachedTokens || 0;
  const uncachedPromptTokens = Math.max(0, usage.promptTokens - cachedTokens);
  const cachedRate = pricing.cachedInputPerMillion ?? pricing.inputPerMillion;

  return Number((
    (uncachedPromptTokens / 1_000_000) * pricing.inputPerMillion +
    (cachedTokens / 1_000_000) * cachedRate +
    (usage.completionTokens / 1_000_000) * pricing.outputPerMillion
  ).toFixed(12));
}

export class NovitaProvider implements Provider {
  readonly name = 'novita';
  readonly specificationVersion = '1' as const;

  private config: ProviderFactoryConfig;

  private static modelsCache: NovitaModelInfo[] | null = null;
  private static modelsCacheTime = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000;
  private static readonly USAGE_PRICING_FINISH_WAIT_MS = 2500;

  constructor(config: ProviderFactoryConfig) {
    this.config = config;
  }

  supportsModel(_modelId: string): boolean {
    return true;
  }

  getIcon(modelId?: string): string {
    return getNovitaModelIconDataUri(modelId) || getNovitaIconDataUri();
  }

  private baseUrl(): string {
    return trimTrailingSlashes(this.config.baseUrl || DEFAULT_BASE_URL);
  }

  private headers(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.config.apiKey}`,
    };
  }

  private async fetchModelsWithCache(): Promise<NovitaModelInfo[]> {
    const now = Date.now();

    if (
      NovitaProvider.modelsCache &&
      now - NovitaProvider.modelsCacheTime < NovitaProvider.CACHE_TTL
    ) {
      return NovitaProvider.modelsCache;
    }

    const response = await fetch(`${this.baseUrl()}/models`, {
      method: 'GET',
      headers: this.headers(),
    });

    if (!response.ok) {
      throw await this.toHttpProviderErrorFromResponse(response, 'Novita models API error');
    }

    const data = await response.json() as { data?: NovitaModelInfo[] };
    const merged = new Map<string, NovitaModelInfo>();

    for (const model of STATIC_MODELS) {
      merged.set(model.id, model);
    }

    for (const model of data.data || []) {
      merged.set(model.id, { ...merged.get(model.id), ...model });
    }

    NovitaProvider.modelsCache = Array.from(merged.values());
    NovitaProvider.modelsCacheTime = now;

    return NovitaProvider.modelsCache;
  }

  private mapModelCapabilities(model: NovitaModelInfo): ModelCapabilities {
    return {
      supportsImages: isVisionModel(model),
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: model.context_size,
    };
  }

  async getModelCapabilities(modelId: string): Promise<ModelCapabilities | null> {
    try {
      const models = await this.fetchModelsWithCache();
      const model = models.find((candidate) => candidate.id === modelId);
      if (!model) return null;
      return this.mapModelCapabilities(model);
    } catch (error) {
      console.error(`Failed to fetch capabilities for Novita model ${modelId}:`, error);
      const staticModel = STATIC_MODELS.find((candidate) => candidate.id === modelId);
      return staticModel ? this.mapModelCapabilities(staticModel) : null;
    }
  }

  private mapToProviderModelInfo(model: NovitaModelInfo): ProviderModelInfo {
    const pricing = novitaUsagePricingFromModel(model);

    return {
      id: model.id,
      name: model.title || model.id,
      description: model.description,
      contextLength: model.context_size,
      iconId: this.getIcon(model.id),
      slug: model.id,
      inputPrice: pricing?.inputPerMillion,
      outputPrice: pricing?.outputPerMillion,
      cachedPrice: pricing?.cachedInputPerMillion,
      capabilities: this.mapModelCapabilities(model),
    };
  }

  async getModels(filter?: string): Promise<ProviderModelInfo[]> {
    try {
      let models = (await this.fetchModelsWithCache())
        .map((model) => this.mapToProviderModelInfo(model))
        .sort((a, b) => a.name.localeCompare(b.name));

      if (filter) {
        const lowerFilter = filter.toLowerCase();
        models = models.filter(
          (model) =>
            model.id.toLowerCase().includes(lowerFilter) ||
            model.name.toLowerCase().includes(lowerFilter) ||
            (model.description && model.description.toLowerCase().includes(lowerFilter))
        );
      }

      return models;
    } catch (error) {
      console.error('Failed to fetch models from Novita:', error);
      return STATIC_MODELS.map((model) => this.mapToProviderModelInfo(model));
    }
  }

  private static async settleUsagePricing(
    pricingPromise: Promise<ProviderUsagePricing | undefined>
  ): Promise<ProviderUsagePricing | undefined> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<undefined>((resolve) => {
      timer = setTimeout(() => resolve(undefined), NovitaProvider.USAGE_PRICING_FINISH_WAIT_MS);
    });
    try {
      return await Promise.race([pricingPromise, timeout]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  private async getUsagePricing(
    modelId: string | undefined
  ): Promise<ProviderUsagePricing | undefined> {
    if (!modelId) return undefined;
    try {
      const models = await this.fetchModelsWithCache();
      const model = models.find((candidate) => candidate.id === modelId);
      return novitaUsagePricingFromModel(model);
    } catch {
      const staticModel = STATIC_MODELS.find((candidate) => candidate.id === modelId);
      return novitaUsagePricingFromModel(staticModel);
    }
  }

  async generate(request: ProviderRequest): Promise<ProviderResponse> {
    const pricingPromise = this.getUsagePricing(request.model);

    try {
      const response = await fetch(`${this.baseUrl()}/chat/completions`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(buildChatParams(request)),
        signal: request.signal,
      });

      if (!response.ok) {
        throw await this.toHttpProviderErrorFromResponse(response, 'Novita API error');
      }

      const pricing = await NovitaProvider.settleUsagePricing(pricingPromise);
      const data = await response.json();
      return transformChatResponse(data, pricing);
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  async stream(request: ProviderRequest): Promise<AsyncIterable<ProviderStreamChunk>> {
    const self = this;
    const pricingPromise = this.getUsagePricing(request.model);

    try {
      const response = await fetch(`${this.baseUrl()}/chat/completions`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify({
          ...buildChatParams(request),
          stream: true,
          stream_options: { include_usage: true },
        }),
        signal: request.signal,
      });

      if (!response.ok) {
        throw await this.toHttpProviderErrorFromResponse(response, 'Novita API error');
      }

      return {
        async *[Symbol.asyncIterator]() {
          const state = createChatStreamState(request.model);

          try {
            for await (const chunk of parseChatSSEStream(response, state)) {
              if (chunk.type === 'finish') {
                const pricing = await NovitaProvider.settleUsagePricing(pricingPromise);
                if (pricing) attachUsagePricing(chunk.usage, pricing);
              }
              yield chunk;
            }
          } catch (error) {
            const providerError = self.toProviderError(error);
            yield createChatErrorChunk(providerError.message, providerError.code);
          }
        },
      };
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  private async toHttpProviderErrorFromResponse(
    response: Response,
    fallbackPrefix: string
  ): Promise<ProviderError> {
    const errorText = await response.text();
    let errorMessage = `${fallbackPrefix}: ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText) as {
        error?: { message?: string };
        message?: string;
      };
      errorMessage = errorJson.error?.message || errorJson.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    return this.toHttpProviderError(errorMessage, response.status, response.headers);
  }

  private toProviderError(error: unknown): ProviderError {
    if (error instanceof ProviderError) {
      return error;
    }

    if (error instanceof Error) {
      const anyError = error as Error & {
        status?: number;
        statusCode?: number;
        headers?: Record<string, string>;
        code?: string;
      };

      const status = anyError.status || anyError.statusCode;
      const retryAfter = anyError.headers?.['retry-after']
        ? parseInt(anyError.headers['retry-after'], 10)
        : undefined;

      if (status === 429) {
        return new ProviderError(error.message, 'rate_limit', status, retryAfter);
      }
      if (status === 401 || status === 403) {
        return new ProviderError(error.message, 'auth_error', status);
      }
      if (status === 400 || status === 404) {
        return new ProviderError(error.message, 'invalid_request', status);
      }
      if (status && status >= 500) {
        return new ProviderError(error.message, 'server_error', status);
      }
      if (error.name === 'AbortError' || anyError.code === 'ETIMEDOUT') {
        return new ProviderError(error.message, 'timeout');
      }

      return new ProviderError(error.message, 'unknown', status);
    }

    return new ProviderError(String(error), 'unknown');
  }

  private toHttpProviderError(
    message: string,
    status: number,
    headers?: Headers
  ): ProviderError {
    const retryAfterHeader = headers?.get('retry-after');
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;

    if (status === 429) {
      return new ProviderError(message, 'rate_limit', status, retryAfter);
    }
    if (status === 401 || status === 403) {
      return new ProviderError(message, 'auth_error', status);
    }
    if (status === 400 || status === 404) {
      return new ProviderError(message, 'invalid_request', status);
    }
    if (status >= 500) {
      return new ProviderError(message, 'server_error', status);
    }

    return new ProviderError(message, 'unknown', status);
  }

  async inspectRequest(request: ProviderRequest): Promise<InspectedRequest> {
    const params = buildChatParams(request);

    return {
      body: truncateChatBase64(params as Record<string, unknown>),
      messagesPath: 'messages',
      metadata: {
        endpoint: 'POST /openai/v1/chat/completions',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    };
  }
}
